import React, { useEffect, useState } from 'react';
import FormError from '../../../FormError';
import CollapsibleCardHeader from './CollapsibleCardHeader';
import { apiFetch, errorText } from '../shared/api';
import { formUrlEncoded } from './format';
import Spinner from '../shared/Spinner';

function fieldError(errors, field) {
    return errors && errors[field] && errors[field][0];
}

function toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

// A cleared input counts as 0; anything that is not a whole number of at least 0 is not a usable weight.
function validWeight(value) {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

// A server list that is a strict subset of the held ids is a deletion: keep the surviving local rows so unsaved edits are not lost.
export function reconcileRows(currentRows, serverLinks) {
    const heldIds = new Set(currentRows.map((row) => row.id));
    const isDeletion = serverLinks.length < currentRows.length && serverLinks.every((link) => heldIds.has(link.id));

    if (isDeletion) {
        const serverIds = new Set(serverLinks.map((link) => link.id));
        return currentRows.filter((row) => serverIds.has(row.id));
    }

    return serverLinks.map((link) => ({ ...link }));
}

export default function ReviewLinksCard({
    demo,
    endpoints,
    redirectAlgorithms,
    weightedAlgorithms,
    reviewSites,
    onDemoPatch,
    onToast,
}) {
    const [rows, setRows] = useState(() => demo.review_links.map((link) => ({ ...link })));
    const [newRows, setNewRows] = useState([]);
    const [fiveStarMinimum, setFiveStarMinimum] = useState(Boolean(demo.review_settings.five_star_minimum));
    const [algorithm, setAlgorithm] = useState(demo.review_settings.auto_redirect_algo);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [collapsed, setCollapsed] = useState(true);
    const [ordering, setOrdering] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        setRows((current) => reconcileRows(current, demo.review_links));
    }, [demo.review_links]);

    useEffect(() => {
        setFiveStarMinimum(Boolean(demo.review_settings.five_star_minimum));
        setAlgorithm(demo.review_settings.auto_redirect_algo);
    }, [demo.review_settings]);

    const isWeighted = weightedAlgorithms.includes(Number(algorithm));

    const updateRow = (id, field, value) => {
        setRows((current) => current.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    };

    const updateNewRow = (index, field, value) => {
        setNewRows((current) => current.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    // Active existing rows plus every pending new row count toward the 100 total.
    // Every row's weight is sent on save, so an unusable weight on any row must
    // block Save, since typing is never clamped.
    const countedWeights = [
        ...rows.filter((row) => row.active).map((row) => validWeight(row.weight)),
        ...newRows.map((row) => validWeight(row.weight)),
    ];
    const hasInvalidWeight = countedWeights.includes(null)
        || rows.some((row) => validWeight(row.weight) === null);
    const activeWeightSum = countedWeights.reduce((sum, weight) => sum + (weight ?? 0), 0);
    const weightRemaining = 100 - activeWeightSum;

    // A pending (unsaved) row can be dropped on the client before it is ever sent.
    const removeNewRow = (index) => {
        setNewRows((current) => current.filter((_, i) => i !== index));
    };

    const persistOrder = (orderedRows, previousRows) => {
        const body = formUrlEncoded(orderedRows.map((row) => ['item[]', row.id]));

        setOrdering(true);
        apiFetch(endpoints.reviewLinksSort, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
        }).then(({ status, json }) => {
            if (status >= 200 && status < 300 && Array.isArray(json?.data?.review_links)) {
                onDemoPatch(json.data);
            } else {
                setRows(previousRows);
                onToast({ type: 'error', text: errorText(json, __('Something went wrong. Please try again.')) });
            }
        }).catch((error) => {
            console.error(error);
            setRows(previousRows);
            onToast({ type: 'error', text: __('Something went wrong. Please try again.') });
        }).finally(() => {
            setOrdering(false);
        });
    };

    const reorderAndPersist = (from, to) => {
        if (deletingId !== null || ordering || saving || from === null || from === to || to < 0 || to >= rows.length) {
            return;
        }

        const previousRows = rows;
        const reordered = [...rows];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        setRows(reordered);
        persistOrder(reordered, previousRows);
    };

    const handleDragStart = (index) => (event) => {
        if (ordering || saving || deletingId !== null) {
            event.preventDefault();
            return;
        }
        setDragIndex(index);
        setDragOverIndex(index);
        event.dataTransfer.effectAllowed = 'move';
        // Firefox only starts a drag once some data is attached to the transfer.
        event.dataTransfer.setData('text/plain', String(index));
    };

    const handleDragOver = (index) => (event) => {
        if (dragIndex === null) {
            return;
        }
        // preventDefault marks this row as a valid drop target.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (index !== dragOverIndex) {
            setDragOverIndex(index);
        }
    };

    const handleDrop = (index) => (event) => {
        event.preventDefault();
        const from = dragIndex;
        // With only two short rows a user often releases while still over the source
        // row, so the drop fires on the source; the last row dragged over is the target.
        const to = dragOverIndex ?? index;
        setDragIndex(null);
        setDragOverIndex(null);
        reorderAndPersist(from, to);
    };

    const handleDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    };

    const addSite = (site) => {
        setNewRows((current) => [...current, {
            type: 'predefined',
            name: site.name,
            logo: site.logo,
            logoUrl: site.logoUrl,
            settings: site.settings,
            url: site.settings?.search ? '' : site.url,
            weight: 0,
        }]);
        setPickerOpen(false);
    };

    const addCustomSite = () => {
        setNewRows((current) => [...current, {
            type: 'custom',
            name: '',
            url: '',
            weight: 0,
            file: null,
        }]);
        setPickerOpen(false);
    };

    const handleDelete = async (row) => {
        if (deletingId !== null || ordering || saving) {
            return;
        }

        if (!window.confirm(__('Delete this review link? This cannot be undone.'))) {
            return;
        }

        setDeletingId(row.id);

        try {
            const { status, json } = await apiFetch(
                endpoints.reviewLinksDestroy.replace('__LINK__', encodeURIComponent(row.id)),
                { method: 'DELETE' },
            );

            if (status >= 200 && status < 300 && Array.isArray(json?.data?.review_links)) {
                onDemoPatch({ review_links: json.data.review_links });
                onToast({ type: 'success', text: __('Review link deleted') });
            } else {
                onToast({ type: 'error', text: errorText(json, __('Something went wrong. Please try again.')) });
            }
        } catch (error) {
            console.error(error);
            onToast({ type: 'error', text: __('Something went wrong. Please try again.') });
        } finally {
            setDeletingId(null);
        }
    };

    const handleSave = async () => {
        setErrors({});
        setSuccessMessage(null);
        setSaving(true);

        const body = new FormData();
        body.append('five_star_minimum', fiveStarMinimum ? 'true' : 'false');
        body.append('redirectAlgorithm', String(algorithm));

        rows.forEach((row) => {
            body.append(`links[${row.id}][url]`, row.url || '');
            body.append(`links[${row.id}][weight]`, String(toNumber(row.weight)));
            if (row.active) {
                body.append(`links[${row.id}][active]`, '1');
            }
        });

        newRows.forEach((row, index) => {
            body.append(`linksCustom[${index}][url]`, row.url || '');
            body.append(`linksCustom[${index}][weight]`, String(toNumber(row.weight)));
            body.append(`linksCustom[${index}][active]`, '1');
            if (row.type === 'predefined') {
                body.append(`linksCustom[${index}][logo]`, row.logo);
                body.append(`linksCustom[${index}][settings]`, JSON.stringify(row.settings));
            } else {
                if (row.file) {
                    body.append(`linksCustom[${index}][upload]`, row.file);
                }
                body.append(`linksCustom[${index}][settings]`, JSON.stringify({ name: row.name }));
            }
        });

        try {
            const { status, json } = await apiFetch(endpoints.reviewLinksSave, { method: 'POST', body });

            if (status >= 200 && status < 300) {
                onDemoPatch(json.data);
                setNewRows([]);
                setSuccessMessage(__('Review links saved'));
                onToast({ type: 'success', text: __('Review links saved') });
            } else if (status === 422 && json?.errors) {
                setErrors(json.errors);
            } else {
                onToast({ type: 'error', text: errorText(json, __('Something went wrong. Please try again.')) });
            }
        } catch (error) {
            console.error(error);
            onToast({ type: 'error', text: __('Something went wrong. Please try again.') });
        } finally {
            setSaving(false);
        }
    };

    const fieldErrorEntries = Object.entries(errors)
        .map(([field]) => [field, fieldError(errors, field)])
        .filter(([, message]) => Boolean(message));

    // The Save button covers the link settings (urls, weights, show-in-list, the
    // positive-rating and redirect selects, and any pending new rows). Reordering
    // persists on its own, so row order is not part of this check.
    const linksDirty = rows.length !== demo.review_links.length || rows.some((row) => {
        const original = demo.review_links.find((link) => link.id === row.id);
        if (!original) {
            return true;
        }
        return (row.url || '') !== (original.url || '')
            || Boolean(row.active) !== Boolean(original.active)
            || toNumber(row.weight) !== toNumber(original.weight);
    });
    const isDirty = (
        newRows.length > 0
        || fiveStarMinimum !== Boolean(demo.review_settings.five_star_minimum)
        || Number(algorithm) !== Number(demo.review_settings.auto_redirect_algo)
        || linksDirty
    );
    const weightsBalanced = !hasInvalidWeight && (!isWeighted || activeWeightSum === 100);
    const canSave = isDirty && weightsBalanced;

    return (
        <div className="card demo-edit-form demo-edit-review-links" data-testid="demo-edit-review-links">
            <CollapsibleCardHeader
                title={__('Review Links')}
                collapsed={collapsed}
                onToggle={() => setCollapsed((prev) => !prev)}
                controlsId="demo-edit-review-links-body"
            />
            <div className={`card-body${collapsed ? ' d-none' : ''}`} id="demo-edit-review-links-body">
                {successMessage &&
                    <div className="alert alert-success" data-testid="demo-edit-review-links-success">
                        {successMessage}
                    </div>
                }

                {fieldErrorEntries.length > 0 &&
                    <div data-testid="demo-edit-review-links-errors">
                        {fieldErrorEntries.map(([field, message]) => <FormError key={field} error_message={message} />)}
                    </div>
                }

                <div className="form-group">
                    <label htmlFor="demo-edit-positive-rating">
                        {__('Minimum star rating that counts as a positive review')}
                    </label>
                    {/* Maps to the existing five_star_minimum boolean: 5 stars -> true, 4 -> false. */}
                    <select
                        id="demo-edit-positive-rating"
                        className="form-control demo-edit-redirect-select"
                        value={fiveStarMinimum ? '5' : '4'}
                        onChange={(event) => setFiveStarMinimum(event.target.value === '5')}
                    >
                        <option value="5">{__('5 stars')}</option>
                        <option value="4">{__('4 stars')}</option>
                    </select>
                </div>

                <div className="form-group">
                    <select
                        className="form-control demo-edit-redirect-select"
                        aria-label={__('Redirect algorithm')}
                        value={algorithm}
                        onChange={(event) => setAlgorithm(Number(event.target.value))}
                    >
                        {redirectAlgorithms.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>

                {ordering && <div className="mb-2"><Spinner label={__('Saving order')} /></div>}

                <ul className="list-group demo-edit-review-links-list">
                    {rows.map((row, index) => (
                        <li
                            key={row.id}
                            data-testid={`demo-edit-review-link-${row.id}`}
                            className={`list-group-item demo-edit-review-link-row${dragIndex === index ? ' is-dragging' : ''}${dragOverIndex === index && dragIndex !== index ? ' is-drop-target' : ''}`}
                            onDragOver={handleDragOver(index)}
                            onDrop={handleDrop(index)}
                        >
                            <span
                                className="demo-edit-review-link-handle"
                                role="button"
                                tabIndex={0}
                                aria-label={__('Drag to reorder')}
                                data-testid={`demo-edit-review-link-handle-${row.id}`}
                                draggable={!ordering && !saving}
                                aria-disabled={ordering || saving}
                                onDragStart={handleDragStart(index)}
                                onDragEnd={handleDragEnd}
                            >
                                {'☰'}
                            </span>

                            {isWeighted &&
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    className="form-control demo-edit-review-link-weight"
                                    aria-label={__('Weight')}
                                    value={row.weight ?? 0}
                                    onChange={(event) => updateRow(row.id, 'weight', event.target.value)}
                                />
                            }

                            {row.logo &&
                                <img
                                    src={row.logo}
                                    alt={row.settings?.name}
                                    className="demo-edit-review-link-logo"
                                    style={{ width: 40, height: 40, objectFit: 'contain' }}
                                />
                            }
                            <span className="demo-edit-review-link-name">{row.settings?.name}</span>

                            <input
                                type="url"
                                className="form-control"
                                aria-label={__('URL')}
                                value={row.url || ''}
                                onChange={(event) => updateRow(row.id, 'url', event.target.value)}
                            />

                            <div className="custom-control custom-checkbox">
                                <input
                                    id={`demo-edit-review-link-${row.id}-active`}
                                    type="checkbox"
                                    className="custom-control-input"
                                    checked={Boolean(row.active)}
                                    onChange={(event) => updateRow(row.id, 'active', event.target.checked)}
                                />
                                <label className="custom-control-label" htmlFor={`demo-edit-review-link-${row.id}-active`}>
                                    {__('Show in list')}
                                </label>
                            </div>

                            <button
                                type="button"
                                className="btn btn-link demo-edit-review-link-remove"
                                aria-label={__('Delete review link')}
                                title={__('Delete review link')}
                                data-testid={`demo-edit-review-link-delete-${row.id}`}
                                disabled={deletingId !== null || ordering || saving}
                                onClick={() => handleDelete(row)}
                            >
                                {deletingId === row.id
                                    ? <Spinner label={__('Deleting')} />
                                    : <i className="fas fa-trash" aria-hidden="true" />}
                            </button>
                        </li>
                    ))}

                    {newRows.map((row, index) => (
                        <li
                            key={`new-${index}`}
                            data-testid={`demo-edit-review-link-new-${index}`}
                            className="list-group-item demo-edit-review-link-row demo-edit-review-link-row-new"
                        >
                            {isWeighted &&
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    className="form-control demo-edit-review-link-weight"
                                    aria-label={__('Weight')}
                                    value={row.weight ?? 0}
                                    onChange={(event) => updateNewRow(index, 'weight', event.target.value)}
                                />
                            }

                            {row.type === 'predefined' &&
                                <>
                                    {row.logo &&
                                        <img
                                            src={row.logoUrl || row.logo}
                                            alt={row.name}
                                            className="demo-edit-review-link-logo"
                                            style={{ width: 40, height: 40, objectFit: 'contain' }}
                                        />
                                    }
                                    <span className="demo-edit-review-link-name">{row.name}</span>
                                </>
                            }

                            {row.type === 'custom' &&
                                <div className="demo-edit-review-link-field">
                                    <label htmlFor={`demo-edit-review-link-new-${index}-name`}>{__('Site name')}</label>
                                    <input
                                        id={`demo-edit-review-link-new-${index}-name`}
                                        type="text"
                                        className="form-control"
                                        value={row.name}
                                        onChange={(event) => updateNewRow(index, 'name', event.target.value)}
                                    />
                                </div>
                            }

                            <div className="demo-edit-review-link-field">
                                <label htmlFor={`demo-edit-review-link-new-${index}-url`}>{__('URL')}</label>
                                <input
                                    id={`demo-edit-review-link-new-${index}-url`}
                                    type="url"
                                    className="form-control"
                                    value={row.url}
                                    onChange={(event) => updateNewRow(index, 'url', event.target.value)}
                                />
                            </div>

                            {row.type === 'custom' &&
                                <div className="demo-edit-review-link-field">
                                    <label htmlFor={`demo-edit-review-link-new-${index}-logo`}>{__('Logo')}</label>
                                    <input
                                        id={`demo-edit-review-link-new-${index}-logo`}
                                        type="file"
                                        accept="image/*"
                                        onChange={(event) => updateNewRow(index, 'file', event.target.files && event.target.files[0])}
                                    />
                                </div>
                            }

                            <button
                                type="button"
                                className="btn btn-link demo-edit-review-link-remove"
                                aria-label={__('Remove review site')}
                                title={__('Remove review site')}
                                onClick={() => removeNewRow(index)}
                            >
                                <i className="fas fa-trash" aria-hidden="true" />
                            </button>

                            <p className="text-muted demo-edit-review-link-new-note">
                                {__('New sites are shown in the list once saved')}
                            </p>
                        </li>
                    ))}
                </ul>

                {pickerOpen &&
                    <div data-testid="demo-edit-review-links-picker" className="demo-edit-review-links-picker">
                        {reviewSites.map((site) => (
                            <button
                                key={site.name}
                                type="button"
                                className="btn btn-link"
                                data-testid={`demo-edit-review-site-option-${site.name}`}
                                onClick={() => addSite(site)}
                            >
                                {site.logo &&
                                    <img
                                        src={site.logoUrl || site.logo}
                                        alt={site.name}
                                        className="demo-edit-review-site-option-logo"
                                        style={{ width: 32, height: 32, objectFit: 'contain' }}
                                    />
                                }
                            </button>
                        ))}
                        <button type="button" className="btn btn-link" onClick={addCustomSite}>
                            {__('Custom site')}
                        </button>
                    </div>
                }

                <button type="button" className="btn btn-secondary demo-edit-add-review-site" onClick={() => setPickerOpen(true)}>
                    {__('Add Another Review Site')}
                </button>

                {(isWeighted || hasInvalidWeight) &&
                    <div
                        className={`demo-edit-review-links-weight-indicator${weightsBalanced ? ' text-muted' : ' text-danger'}`}
                        data-testid="demo-edit-review-links-weight-indicator"
                    >
                        {hasInvalidWeight
                            ? __('Weights must be whole numbers of 0 or more')
                            : (activeWeightSum === 100
                                ? __('Weights total 100')
                                : (weightRemaining > 0
                                    ? __(':n left', { ':n': weightRemaining })
                                    : __('over by :n', { ':n': -weightRemaining })))}
                    </div>
                }

                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={saving || ordering || !canSave || deletingId !== null}
                    onClick={handleSave}
                >
                    {saving && <Spinner label={__('Saving')} className="mr-2" />}
                    {__('Save Review Settings')}
                </button>
            </div>
        </div>
    );
}
